import {GoogleOAuthProvider, useGoogleLogin, useGoogleOAuth} from '@react-oauth/google';
import * as FacebookLoginModule from '@greatsumini/react-facebook-login';
import {appleAuthHelpers, useScript} from 'react-apple-signin-auth';
import PropTypes from 'prop-types';
import config from '../../config';
import {newIdempotencyKey} from '../../auth/signup';
import {
  PROVIDER_LABELS,
  SOCIAL_PROVIDERS,
  appleErrorKind,
  appleNames,
  facebookErrorKind,
  googleErrorKind,
  providerConfigured,
  unavailableNote,
} from '../../auth/social';

// @greatsumini/react-facebook-login ships CommonJS with `exports.default`; Vite's ESM interop
// can hand us the module object instead of the component.
const resolveDefault = (m) => (m && typeof m === 'object' && 'default' in m ? resolveDefault(m.default) : m);
const FacebookLogin = resolveDefault(FacebookLoginModule);

// Icons are the legacy app's inline SVGs (dental-book-frontend/src/components/*LoginBtn.jsx).
const ICONS = {
  google: (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        fill="#4285F4"
        d="M22.5 12.27c0-.78-.07-1.53-.2-2.27H12v4.5h5.9c-.26 1.34-1.04 2.48-2.2 3.24v2.69h3.56c2.08-1.92 3.28-4.74 3.28-8.16z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.56-2.69c-.99.66-2.25 1.05-3.72 1.05-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0012 23z"
      />
      <path fill="#FBBC05" d="M5.84 14.17a6.6 6.6 0 010-4.34V7H2.18a11 11 0 000 10z" />
      <path
        fill="#EA4335"
        d="M12 5.46c1.61 0 3.06.55 4.21 1.64l3.15-3.15C17.45 2.18 14.97 1 12 1A11 11 0 002.18 7l3.66 2.83C6.71 7.23 9.14 5.46 12 5.46z"
      />
    </svg>
  ),
  apple: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="#1A1A2E" aria-hidden="true" focusable="false">
      <path d="M17.05 12.04c-.03-2.55 2.08-3.78 2.17-3.84-1.18-1.73-3.03-1.97-3.69-2-1.57-.16-3.06.93-3.86.93-.8 0-2.03-.9-3.34-.88-1.72.03-3.3 1-4.18 2.53-1.79 3.1-.46 7.69 1.27 10.2.85 1.24 1.86 2.62 3.18 2.57 1.28-.05 1.76-.83 3.31-.83s1.98.83 3.33.8c1.38-.02 2.25-1.25 3.09-2.5.97-1.43 1.37-2.83 1.39-2.9-.03-.01-2.67-1.02-2.7-4.06zM14.7 4.27c.71-.86 1.19-2.05 1.06-3.24-1.02.04-2.27.69-3 1.55-.65.75-1.22 1.97-1.07 3.14 1.14.09 2.31-.58 3.01-1.45z" />
    </svg>
  ),
  facebook: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="#1877F2" aria-hidden="true" focusable="false">
      <path d="M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06c0 5.02 3.66 9.19 8.44 9.94v-7.03H7.9v-2.91h2.54V9.85c0-2.51 1.49-3.89 3.77-3.89 1.09 0 2.24.2 2.24.2v2.47h-1.26c-1.24 0-1.63.77-1.63 1.56v1.87h2.77l-.44 2.91h-2.33V22c4.78-.75 8.44-4.92 8.44-9.94z" />
    </svg>
  ),
};

/*
 * Provider adapters (the legacy dental-book-frontend libraries). Each one loads its SDK while
 * the page is open, so the popup opens synchronously inside the click (popup blockers), and
 * hands `start` to its render prop. Results are reported as onToken(provider, {token, ...})
 * or onProviderError(provider, kind) with kind 'cancelled' | 'popup_blocked' | 'unavailable' |
 * 'failed'.
 */

/** Google Identity Services token client (implicit flow -> access_token). */
function GoogleAdapter({onToken, onProviderError, children}) {
  const {scriptLoadedSuccessfully} = useGoogleOAuth();
  const login = useGoogleLogin({
    onSuccess: (response) => {
      if (response && response.access_token) onToken('google', {token: response.access_token});
      else onProviderError('google', 'failed');
    },
    onError: (err) => onProviderError('google', googleErrorKind(err)),
    onNonOAuthError: (err) => onProviderError('google', googleErrorKind(err)),
  });
  const start = () => (scriptLoadedSuccessfully ? login() : onProviderError('google', 'unavailable'));
  return children(start);
}

function GoogleSso({clientId, ...props}) {
  return (
    <GoogleOAuthProvider clientId={clientId}>
      <GoogleAdapter {...props} />
    </GoogleOAuthProvider>
  );
}

/** Sign in with Apple JS in a popup (-> authorization.id_token, user.name on first consent). */
function AppleSso({clientId, redirectUri, onToken, onProviderError, children}) {
  useScript(appleAuthHelpers.APPLE_SCRIPT_SRC);
  const start = () => {
    // Without the SDK the helper would poll for it and the popup would be blocked.
    if (!window.AppleID) {
      onProviderError('apple', 'unavailable');
      return;
    }
    const state = newIdempotencyKey();
    appleAuthHelpers.signIn({
      authOptions: {
        clientId,
        redirectURI: redirectUri,
        scope: 'email name',
        state,
        nonce: newIdempotencyKey(),
        usePopup: true,
      },
      onSuccess: (response) => {
        const auth = (response && response.authorization) || {};
        if (!auth.id_token || (auth.state !== undefined && auth.state !== state)) {
          onProviderError('apple', 'failed');
          return;
        }
        onToken('apple', {token: auth.id_token, ...appleNames(response)});
      },
      onError: (err) => onProviderError('apple', appleErrorKind(err)),
    });
  };
  return children(start);
}

/** Facebook JS SDK login popup (-> authResponse.accessToken). */
function FacebookSso({appId, onToken, onProviderError, children}) {
  return (
    <FacebookLogin
      appId={appId}
      dialogParams={{response_type: 'token'}}
      onSuccess={(response) => {
        if (response && response.accessToken) onToken('facebook', {token: response.accessToken});
        else onProviderError('facebook', 'failed');
      }}
      onFail={(err) => onProviderError('facebook', facebookErrorKind(err))}
      render={({onClick}) => children(onClick)}
    />
  );
}

const ADAPTERS = {
  google: (props) => <GoogleSso clientId={config.googleClientId} {...props} />,
  apple: (props) => (
    <AppleSso clientId={config.appleClientId} redirectUri={config.appleRedirectUri} {...props} />
  ),
  facebook: (props) => <FacebookSso appId={config.facebookAppId} {...props} />,
};

/**
 * One provider's SDK adapter on its own (the sign-in page and the Account page's link buttons
 * share it). `children(start)` renders the trigger; `start` opens the provider popup.
 */
export function SocialAdapter({provider, onToken, onProviderError, children}) {
  const Adapter = ADAPTERS[provider];
  return (
    <Adapter onToken={onToken} onProviderError={onProviderError}>
      {children}
    </Adapter>
  );
}

SocialAdapter.propTypes = {
  provider: PropTypes.oneOf(SOCIAL_PROVIDERS).isRequired,
  onToken: PropTypes.func.isRequired,
  onProviderError: PropTypes.func.isRequired,
  children: PropTypes.func.isRequired,
};

/**
 * Icon-only Google / Apple / Facebook buttons (legacy layout). `verb` builds the accessible
 * name ("Sign in with Google"). While `locked` (terms not accepted) a click calls `onLocked`
 * and never opens the provider; `busy` ignores clicks while a sign-in is being completed. A
 * provider without runtime config renders aria-disabled, described by a visible note.
 */
export default function SsoButtons({
  verb,
  testIdPrefix,
  locked,
  busy,
  describedBy,
  onLocked,
  onToken,
  onProviderError,
}) {
  const unavailable = SOCIAL_PROVIDERS.filter((p) => !providerConfigured(p));
  const noteId = `${testIdPrefix}-unavailable`;

  const renderButton = (provider, start) => {
    const disabled = !start;
    const ids = [describedBy, disabled && noteId].filter(Boolean).join(' ');
    const onClick = () => {
      if (disabled || busy) return;
      if (locked) {
        onLocked();
        return;
      }
      start();
    };
    return (
      <button
        key={provider}
        type="button"
        className={`auth-sso-btn${locked || disabled ? ' is-locked' : ''}`}
        aria-label={`${verb} with ${PROVIDER_LABELS[provider]}`}
        aria-describedby={ids || undefined}
        aria-disabled={disabled ? 'true' : undefined}
        data-testid={`${testIdPrefix}-${provider}`}
        onClick={onClick}
      >
        {ICONS[provider]}
      </button>
    );
  };

  return (
    <>
      <div className="auth-sso-row">
        {SOCIAL_PROVIDERS.map((provider) => {
          if (unavailable.includes(provider)) return renderButton(provider, null);
          return (
            <SocialAdapter
              key={provider}
              provider={provider}
              onToken={onToken}
              onProviderError={onProviderError}
            >
              {(start) => renderButton(provider, start)}
            </SocialAdapter>
          );
        })}
      </div>
      {unavailable.length > 0 && (
        <p id={noteId} className="auth-sso-note" data-testid={noteId}>
          {unavailableNote(unavailable)}
        </p>
      )}
    </>
  );
}

SsoButtons.propTypes = {
  verb: PropTypes.string.isRequired,
  testIdPrefix: PropTypes.string.isRequired,
  locked: PropTypes.bool,
  busy: PropTypes.bool,
  describedBy: PropTypes.string,
  onLocked: PropTypes.func.isRequired,
  onToken: PropTypes.func.isRequired,
  onProviderError: PropTypes.func.isRequired,
};
